// API configuration
import axios from 'axios';
import Cookies from 'js-cookie';
import { DEBUG, BASE_DOMAIN, BASE_URL } from '../conf';
import { ACCESS_TOKEN } from './constants';

// Create a separate instance for session management
const sessionAPI = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    'X-Requested-With': 'XMLHttpRequest'
  }
});

// Add CSRF token to session API if available
sessionAPI.interceptors.request.use(config => {
  const csrfToken = Cookies.get('csrftoken');
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  return config;
});

// State variables for session management
let currentSessionId = null;      // Caches the current session ID
let isSessionFetching = false;    // Prevents multiple concurrent session fetches
const requestQueue = [];          // Queue for requests waiting for session

const isPublicGuestEndpoint = (url = '') => {
  const publicPrefixes = [
    '/api/menu/',
    '/api/faqs/',
    '/api/delivery-rules/',
    '/api/products/',
    '/api/categories/',
    '/api/banners/',
    '/api/search/',
    '/api/blog/',
    '/api/tags/',
    '/api/session/',
  ];
  return publicPrefixes.some((prefix) => url.startsWith(prefix));
};

const isBasketEndpoint = (url = '') => {
  const basketPrefixes = [
    '/api/add-to-basket/',
    '/api/basket/',
  ];
  return basketPrefixes.some((prefix) => url.startsWith(prefix));
};

const isAuthEndpoint = (url = '') => {
  const authPrefixes = [
    '/api/token/',
    '/api/token/refresh/',
    '/api/logout/',
  ];
  return authPrefixes.some((prefix) => url.startsWith(prefix));
};

/**
 * Gets the session ID from cookies or cache
 */
const getStoredSession = () => {
  // First check our cache
  if (currentSessionId) {
    return currentSessionId;
  }
  
  // Then check cookies
  const sessionId = Cookies.get('sessionid');
  if (sessionId) {
    currentSessionId = sessionId;
    return sessionId;
  }
  
  return null;
};

/**
 * Validates or creates a session
 * 
 * @returns {Promise<string>} A promise that resolves to the session ID
 */
const getValidSession = async () => {
  // First check for stored session
  const storedSession = getStoredSession();
  
  if (storedSession) {
    try {
      // Validate existing session
      const response = await sessionAPI.head('/api/session/');
      if (response.status === 200) {
        return storedSession;
      }
    } catch (error) {
      console.log('Session validation failed, will create new one', error);
      currentSessionId = null;
    }
  }

  // If we're already fetching a session, queue this request
  if (isSessionFetching) {
    return new Promise((resolve) => {
      requestQueue.push({ resolve });
    });
  }

  try {
    isSessionFetching = true;
    
    // Create new session with single POST request
    const response = await sessionAPI.post('/api/session/');
    
    // Store both session ID and CSRF token
    currentSessionId = response.data.session_id;
    if (response.data.csrf_token) {
      Cookies.set('csrftoken', response.data.csrf_token, {
        path: '/',
        domain: DEBUG ? undefined : BASE_DOMAIN,
        sameSite: 'lax',
        secure: !DEBUG
      });
    }
    
    return currentSessionId;
    
  } catch (error) {
    console.error('Session error:', error);
    throw error;
  } finally {
    isSessionFetching = false;
    while (requestQueue.length > 0) {
      const { resolve } = requestQueue.shift();
      resolve(currentSessionId);
    }
  }
};

/**
 * Creates the main API instance with interceptors
 * 
 * This function creates an axios instance configured with:
 * 1. Base URL and credentials
 * 2. CSRF token handling
 * 3. Session management
 * 4. Error handling and retries
 * 
 * @returns {AxiosInstance} Configured axios instance
 */
const createAPI = () => {
  const instance = axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
    headers: {
      'X-Requested-With': 'XMLHttpRequest'
    }
  });

  // Request interceptor for session management
  instance.interceptors.request.use(async (config) => {
    // Auth/session endpoints must not depend on a pre-existing storefront session.
    if (config.url.includes('/api/session/') || isAuthEndpoint(config.url || '')) {
      if (isAuthEndpoint(config.url || '') && config.headers?.Authorization) {
        delete config.headers.Authorization;
      }
      return config;
    }

    if (isPublicGuestEndpoint(config.url || '')) {
      const csrfToken = Cookies.get('csrftoken');
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
      if (config.headers?.Authorization) {
        delete config.headers.Authorization;
      }
      return config;
    }

    try {
      if (isBasketEndpoint(config.url || '')) {
        const sessionId = getStoredSession() || await getValidSession();
        const csrfToken = Cookies.get('csrftoken');
        if (csrfToken) {
          config.headers['X-CSRFToken'] = csrfToken;
        }
        const accessToken = localStorage.getItem(ACCESS_TOKEN);
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        } else if (config.headers?.Authorization) {
          delete config.headers.Authorization;
        }
        if (sessionId) {
          config.headers['X-Session-Id'] = sessionId;
        }
        return config;
      }

      // Get current session or create new one
      const sessionId = await getValidSession();
      
      // Add headers
      const csrfToken = Cookies.get('csrftoken');
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
      const accessToken = localStorage.getItem(ACCESS_TOKEN);
      if (accessToken && !isPublicGuestEndpoint(config.url || '')) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      } else if (config.headers?.Authorization) {
        delete config.headers.Authorization;
      }
      if (sessionId) {
        config.headers['X-Session-Id'] = sessionId;
      }
      
      return config;
    } catch (error) {
      console.error('Session management failed:', error);
      return Promise.reject(new Error('No valid session available'));
    }
  });

  // Response interceptor for session errors
  instance.interceptors.response.use(
    response => response,
    async error => {
      const requestUrl = error.config?.url || '';
      const isCSRFError = error.response?.data?.detail?.includes('CSRF');
      
      if (
        (isCSRFError || ((error.response?.status === 401 || error.response?.status === 403) && !isAuthEndpoint(requestUrl))) &&
        !error.config?._retry
      ) {
        try {
          error.config._retry = true;
          // Clear current session and CSRF token
          currentSessionId = null;
          Cookies.remove('csrftoken');

          // Guest/public endpoints should not retry with stale auth header.
          if (isPublicGuestEndpoint(requestUrl)) {
            if (error.config.headers?.Authorization) {
              delete error.config.headers.Authorization;
            }
          }
          
          // Get new session and CSRF token
          const sessionId = await getValidSession();
          if (sessionId) {
            // Update the request with new session and CSRF token
            error.config.headers['X-Session-Id'] = sessionId;
            const csrfToken = Cookies.get('csrftoken');
            if (csrfToken) {
              error.config.headers['X-CSRFToken'] = csrfToken;
            }
            return instance(error.config);
          }
        } catch (retryError) {
          console.error('Failed to get new session:', retryError);
        }
      }
      return Promise.reject(error);
    }
  );
  
  return instance;
};

// Create and configure the API instance
const api = createAPI();

// Configure session API error handling
sessionAPI.interceptors.response.use(
  response => response,
  error => {
    console.error('Session API error:', error);
    return Promise.reject(error);
  }
);

export const clearSessionStorage = () => {
  console.log('Clearing all session storage');
  try {
    const cookieOptions = {
      path: '/',
      domain: DEBUG ? undefined : BASE_DOMAIN,
      sameSite: 'lax',
      secure: !DEBUG
    };

    Cookies.remove('sessionid', cookieOptions);
    
    currentSessionId = null;
    console.log('All session storage cleared');
  } catch (error) {
    console.error('Error clearing session storage:', error);
  }
};

export { api };
export default api;
